import React from "react";
import { ShieldCheck, Droplets, Scale, Award, Layers, FlaskConical, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TankingSafety() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const features = [{
    icon: <ShieldCheck className="size-8 text-primary" />,
    title: "Advanced Tanking Systems",
    description: "We utilize multi-layer hydrostatic waterproofing membranes to create a completely sealed barrier behind your tiles, preventing any water ingress."
  }, {
    icon: <Droplets className="size-8 text-primary" />,
    title: "100% Leak-Proof Guarantee",
    description: "Our wet room installations are rigorously tested. We provide a comprehensive guarantee against leaks, giving you total peace of mind."
  }, {
    icon: <Scale className="size-8 text-primary" />,
    title: "Precision Leveling",
    description: "Using laser levels and self-leveling compounds, we ensure perfectly flat substrates and precise drainage gradients for efficient water flow."
  }, {
    icon: <Award className="size-8 text-primary" />,
    title: "10-Year Warranty",
    description: "Backed by over 11 years of experience, our workmanship comes with a solid warranty. We stand by the durability of every project."
  }];
  const technicalSpecs = [{
    title: "Ditra Matting Installation",
    description: "The ultimate uncoupling membrane. Ditra matting neutralizes stresses between the substrate and the tile, preventing cracks and delamination. Essential for underfloor heating and timber floors.",
    icon: <Layers className="size-6 text-secondary-foreground" />,
    bgClass: "bg-secondary",
    textClass: "text-secondary-foreground"
  }, {
    title: "Epoxy Grout Finishing",
    description: "Unlike standard grout, epoxy is non-porous, stain-resistant, and incredibly durable. It creates a hygienic, waterproof seal that never discolors and withstands heavy cleaning.",
    icon: <FlaskConical className="size-6 text-primary-foreground" />,
    bgClass: "bg-primary",
    textClass: "text-primary-foreground"
  }];
  return <section id="tanking-safety" ref={ref} className={`dark bg-background text-foreground py-20 transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <style dangerouslySetInnerHTML={{
      __html: `
        :root {
          --background: hsl(0 0% 100%);
          --foreground: hsl(0 0% 95%);
          --card-foreground: hsl(0 0% 95%);
          --popover-foreground: hsl(0 0% 95%);
          --primary-foreground: hsl(0 0% 95%);
          --secondary-foreground: hsl(0 0% 95%);
          --muted-foreground: hsl(0 0% 95%);
          --sidebar-foreground: hsl(0 0% 95%);
        }
        .dark {
          --foreground: hsl(0 0% 95%);
          --card-foreground: hsl(0 0% 95%);
          --popover-foreground: hsl(0 0% 95%);
          --primary-foreground: hsl(0 0% 95%);
          --secondary-foreground: hsl(0 0% 95%);
          --muted-foreground: hsl(0 0% 95%);
          --sidebar-foreground: hsl(0 0% 95%);
        }
        body { text-align: left; }
        .container { margin-left: 0; margin-right: auto; }
        .flex, .grid { justify-content: flex-start; }
      `
    }} />
      <div className="container ml-0 mr-auto px-4 md:px-6">
        {}
        <div className="max-w-3xl text-left mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
            Engineered for Safety & Longevity
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            A beautiful bathroom means nothing if it isn't built to last. At SimProTiling, we prioritize what goes 
            <span className="text-primary font-medium"> behind the tiles</span> as much as the finish itself. 
            Our technical waterproofing ensures your home remains safe and dry.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {features.map((feature, i) => <Card key={i} data-index={i} className="bg-card border-border/50 hover:border-primary/50 transition-colors duration-300">
              <CardHeader>
                <div className="mb-4 p-3 bg-accent/10 w-fit rounded-lg border border-accent/20">
                  {feature.icon}
                </div>
                <CardTitle className="text-xl mb-2">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-muted-foreground text-base">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>)}
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {technicalSpecs.map((spec, i) => <div key={i} data-index={i} className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 md:p-10 flex flex-col justify-start group">
              <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity duration-500`}>
                {}
                {React.cloneElement(spec.icon as React.ReactElement, {
              className: "size-48"
            })}
              </div>
              
              <div className="relative z-10">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-6 w-fit ${spec.bgClass} ${spec.textClass}`}>
                  {spec.icon}
                  <span>Technical Standard</span>
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
                  {spec.title}
                </h3>
                
                <p className="text-muted-foreground text-lg mb-8 max-w-md">
                  {spec.description}
                </p>

                <div className="flex items-center gap-4">
                  <Link to="#">
                    <Button variant="outline" className="border-primary/50 hover:bg-primary hover:text-primary-foreground transition-all">
                      Learn More
                    </Button>
                  </Link>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-16 text-left">
          <p className="text-muted-foreground mb-6">
            Ready to start your renovation with a team that guarantees safety?
          </p>
          <Link to="#">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8">
              Get a Free Technical Consultation <ArrowRight className="ml-2 size-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}